import React, { useEffect, useRef, useState } from 'react';
import { CheckCircle2, FileDown, Loader2, Pencil, TriangleAlert } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { errMsg } from '@/services/staffAttendanceApi';
import { ewbApi, ewbDocsApi, ewbInwardApi, ewbPdf } from '@/services/ewayBillApi';
import { fmtDT, ModeBadge } from '@/components/ewayBill/shared';
import { DocPicker } from '@/components/ewayBill/DocPicker';
import { EwbReview } from '@/components/ewayBill/EwbReview';
import { PartBForm, partBProblems } from '@/components/ewayBill/PartBForm';
import { TallyNames } from '@/components/ewayBill/TallyNames';
import { ManualDcPanel } from '@/components/ewayBill/ManualDcPanel';
import { JobberFixCard } from '@/components/ewayBill/JobberFixCard';
import { DirectShipToPanel } from '@/components/ewayBill/DirectShipToPanel';

const NO_PB = { trip_id: null, vehicle_no: '', distance_km: '', transporter_id: '', transporter_name: '', transport_id: null };
const NO_OPTS = { unit_id: null, to_unit_id: null, to_jobber: null, sub_supply_type: null, sub_supply_desc: '', customer_id: null };
const SUGGEST_KEYS = ['transporter_name', 'transporter_id', 'distance_km'];

const Problems = ({ list }) => (list.length ? (
  <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900" data-testid="ewb-problems">
    <div className="flex items-center gap-1 font-semibold"><TriangleAlert className="w-4 h-4" />Fix before generating</div>
    <ul className="list-disc pl-5">{list.map((p) => <li key={p}>{p}</li>)}</ul>
  </div>
) : null);

const Done = ({ b, onAgain }) => (
  <div className="py-6 text-center space-y-3" data-testid="ewb-done">
    <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-600" />
    <div className="text-sm text-gray-500">E-way bill generated <ModeBadge mode={b.mode} /></div>
    <div className="font-mono text-3xl font-bold tracking-wider" data-testid="ewb-done-no">{b.ewb_no}</div>
    <div className="text-sm">Valid until <b data-testid="ewb-done-valid">{fmtDT(b.valid_upto)}</b></div>
    <div className="flex justify-center gap-2">
      <Button onClick={() => ewbPdf(b).catch((e) => toast.error(errMsg(e, 'PDF failed')))} className="bg-[#2D5F4F] hover:bg-[#234A3D]" data-testid="ewb-done-pdf"><FileDown className="w-4 h-4 mr-1" />Print PDF</Button>
      <Button variant="outline" onClick={onAgain} data-testid="ewb-done-another">Create another</Button>
    </div>
  </div>
);

const Queued = ({ b, onAgain }) => (
  <div className="py-6 text-center space-y-3" data-testid="ewb-done-queued">
    <CheckCircle2 className="w-12 h-12 mx-auto text-teal-600" />
    <div className="text-lg font-semibold">Sent to the Tally queue</div>
    <p className="mx-auto max-w-lg text-sm text-gray-600">
      The Tally link will create <b>{b.tally?.vch_type} {b.doc_no}</b> in Tally within a few seconds. Then in TallyPrime press
      <b> Alt+G → e-Way Bill → Exchange → Send for e-Way Bill</b>. The e-way bill number shows up here on its own.
    </p>
    <Button variant="outline" onClick={onAgain} data-testid="ewb-done-another">Create another</Button>
  </div>
);

const Awaiting = ({ b, onAgain }) => (
  <div className="py-6 text-center space-y-3" data-testid="ewb-done-awaiting">
    <CheckCircle2 className="w-12 h-12 mx-auto text-indigo-600" />
    <div className="text-lg font-semibold">Saved — waiting for the e-way bill</div>
    <p className="mx-auto max-w-lg text-sm text-gray-600">
      Tick <b>{b.doc_no}</b> in the list → <b>Download portal JSON</b> → upload it on the GST portal (e-Waybill → Bulk Generation) →
      import the portal's results file here. The bill turns Active with its EWB No.
    </p>
    <Button variant="outline" onClick={onAgain} data-testid="ewb-done-another">Create another</Button>
  </div>
);

/** New e-way bill: pick document → review ERP Part A + fill Part B → generate (GSP), send to Tally, or save for the portal JSON. */
export const NewEwbDialog = ({ open, onClose, cfg, prefill, onCreated }) => {
  const viaTally = cfg?.route === 'tally';
  const firstType = cfg?.doc_types?.[0]?.id || 'customer_invoice';
  const [docType, setDocType] = useState(firstType);
  const [docNo, setDocNo] = useState('');
  const [source, setSource] = useState(null);
  const [draft, setDraft] = useState(null);
  const [opts, setOpts] = useState(NO_OPTS);
  const [pb, setPb] = useState(NO_PB);
  const [trips, setTrips] = useState([]);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [manual, setManual] = useState(null);
  const auto = useRef(new Set());
  const refId = useRef(null); // saved inward bill id — two suppliers may share a bill number
  const reset = () => { setDraft(null); setDone(null); setOpts(NO_OPTS); setPb(NO_PB); setSource(null); setManual(null); auto.current.clear(); refId.current = null; };
  useEffect(() => {
    if (!open) return;
    reset();
    setDocType(prefill?.doc_type || firstType);
    setDocNo(prefill?.doc_no || '');
    ewbApi.trips().then(setTrips).catch(() => setTrips([]));
    if (prefill?.auto && prefill.doc_no) fetchDraft(NO_OPTS, prefill.doc_no, prefill.doc_type);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const seq = useRef(0);
  const timer = useRef(null);
  const pbRef = useRef(pb);
  pbRef.current = pb;
  // Part B pre-fill (pick-slip transporter, PIN-to-PIN distance): fills empty fields and keeps refreshing the ones it filled until the user types.
  const applySuggest = (s) => {
    const p = pbRef.current;
    const next = { ...p };
    SUGGEST_KEYS.forEach((k) => {
      const val = !s || s[k] == null || s[k] === '' ? '' : String(s[k]);
      if (p[k] && !auto.current.has(k)) return;
      next[k] = val;
      if (val) auto.current.add(k); else auto.current.delete(k);
    });
    pbRef.current = next;
    setPb(next);
  };
  const editPb = (v) => { SUGGEST_KEYS.forEach((k) => v[k] !== pb[k] && auto.current.delete(k)); setPb(v); };
  const fetchDraft = (o = opts, no = docNo, type = docType) => {
    const n = ++seq.current;
    setBusy(true);
    ewbApi.draft({ doc_type: type, doc_no: no, ...o, inward_id: type === 'inward' ? refId.current : null }).then((d) => { if (n === seq.current) { setDraft(d); applySuggest(d.part_b_suggest); } })
      .catch((e) => n === seq.current && toast.error(errMsg(e, 'Could not fetch the document')))
      .finally(() => n === seq.current && setBusy(false));
  };
  const changeOpts = (o) => {
    const typing = o.sub_supply_desc !== opts.sub_supply_desc;
    setOpts(o);
    clearTimeout(timer.current);
    if (typing) timer.current = setTimeout(() => fetchDraft(o), 400);
    else fetchDraft(o);
  };
  const pbBad = Object.values(partBProblems(pb)).some(Boolean);
  const pickedVeh = useRef(null);
  const pick = (d) => { setDocNo(d.doc_no); setSource(d.source); refId.current = d.source?.kind === 'ewb_inward_docs' ? d.source.ref : null; pickedVeh.current = { doc_no: d.doc_no, vehicle_no: d.vehicle_no }; };
  const fetchDoc = () => {
    const v = pickedVeh.current;
    if (v?.vehicle_no && v.doc_no === docNo) setPb((p) => ({ ...p, vehicle_no: p.vehicle_no || v.vehicle_no }));
    fetchDraft();
  };
  const inward = docType === 'inward';
  const manualSaved = (m) => {
    setManual(null); setDocNo(m.doc_no);
    refId.current = inward ? m.id : null;
    setSource(inward ? { kind: 'ewb_inward_docs', ref: m.id } : { kind: 'ewb_manual_docs', ref: m.doc_no });
    fetchDraft(opts, m.doc_no);
  };
  const dsUse = (t) => { setManual(null); setDocNo(t.doc_no); setSource({ kind: 'ewb_direct_ship_tos', ref: t.doc_no }); fetchDraft(opts, t.doc_no); };
  const editManual = () => (inward ? ewbInwardApi.get(draft.inward_id) : ewbDocsApi.getManual(draft.doc_no)).then((m) => { setDraft(null); setManual({ initial: m }); })
    .catch((e) => toast.error(errMsg(e, 'Could not open the saved document')));
  const generate = () => {
    setBusy(true);
    ewbApi.generate({ doc_type: docType, doc_no: docNo, ...opts, inward_id: inward ? draft.inward_id : null, sub_supply_type: draft.sub_supply_type, source, part_b: { ...pb, distance_km: Number(pb.distance_km) || 0 } })
      .then((b) => { setDone(b); onCreated(); toast.success(b.ewb_no ? `E-way bill ${b.ewb_no} generated` : b.status === 'AWAITING' ? 'Saved — waiting for the portal JSON' : 'Sent to the Tally queue'); })
      .catch((e) => toast.error(errMsg(e, 'Generate failed'), { duration: 8000 })).finally(() => setBusy(false));
  };
  const step = done ? 'done' : draft ? 'review' : 'doc';
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className={`${manual ? 'max-w-6xl' : 'max-w-4xl'} max-h-[92vh] overflow-y-auto`} data-testid="ewb-new-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2">New e-way bill {cfg && <ModeBadge mode={cfg.mode} />}</DialogTitle></DialogHeader>
        {step === 'done' && (done.status === 'QUEUED' ? <Queued b={done} onAgain={() => { reset(); setDocNo(''); }} />
          : done.status === 'AWAITING' ? <Awaiting b={done} onAgain={() => { reset(); setDocNo(''); }} />
            : <Done b={done} onAgain={() => { reset(); setDocNo(''); }} />)}
        {step === 'doc' && manual && docType === 'direct_ship' && <DirectShipToPanel onUse={dsUse} onCancel={() => setManual(null)} />}
        {step === 'doc' && manual && docType !== 'direct_ship' && (
          <ManualDcPanel key={manual.initial?.id || manual.initial?.doc_no || 'new'} initial={manual.initial} states={cfg?.states} units={cfg?.units || []} inward={inward}
            onSaved={manualSaved} onCancel={() => setManual(null)} />
        )}
        {step === 'doc' && !manual && (
          <>
            <DocPicker cfg={cfg} docType={docType} setDocType={(v) => { setDocType(v); setSource(null); refId.current = null; }} docNo={docNo}
              setDocNo={(v) => { setDocNo(v); setSource(null); refId.current = null; }}
              onPick={pick} pickedRef={source?.ref} onManual={() => setManual({ initial: null })} onUploaded={onCreated} />
            <div className="flex justify-end"><Button onClick={fetchDoc} disabled={busy || !docNo.trim()} className="bg-[#1A1F2E] hover:bg-black" data-testid="ewb-fetch-btn">
              {busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}Fetch details from ERP</Button></div>
          </>
        )}
        {step === 'review' && (
          <div className="space-y-3">
            {draft.existing && <p className="rounded-lg bg-rose-50 border border-rose-200 px-3 py-2 text-sm text-rose-800" data-testid="ewb-existing">An e-way bill ({draft.existing.ewb_no || draft.existing.status.replace('_', ' ').toLowerCase()}) already exists for this document — cancel or discard it first to make a new one.</p>}
            {['manual_dc', 'inward'].includes(draft.doc_type) && (
              <div className="flex justify-end"><Button size="sm" variant="outline" onClick={editManual} data-testid="ewb-manual-edit-btn"><Pencil className="w-4 h-4 mr-1" />{draft.doc_type === 'inward' ? 'Edit inward bill' : 'Edit manual DC'}</Button></div>
            )}
            <EwbReview route={cfg?.route} draft={draft} units={cfg?.units || []} opts={{ ...opts, sub_supply_type: draft.sub_supply_type }} setOpts={changeOpts} />
            {draft.jobber_fix && <JobberFixCard fix={draft.jobber_fix} onSaved={() => fetchDraft()} />}
            {viaTally && draft.tally && <TallyNames tally={draft.tally} onMapped={() => fetchDraft()} />}
            {draft.gate_count > 0 && !draft.problems.length && (
              <p className="rounded-lg border border-teal-200 bg-teal-50 px-3 py-2 text-xs text-teal-900" data-testid="ewb-gate-note">
                Gate Outward lets these <b>{draft.gate_count}</b> bundle(s) / box(es) / pieces out once the e-way bill number is in WMS.</p>
            )}
            <PartBForm value={pb} onChange={editPb} trips={trips} dist={draft.part_b_suggest?.distance} />
            <Problems list={draft.problems} />
            <div className="flex justify-between">
              <Button variant="ghost" onClick={() => { clearTimeout(timer.current); seq.current += 1; setBusy(false); setDraft(null); setPb(NO_PB); setOpts(NO_OPTS); auto.current.clear(); }} data-testid="ewb-back-btn">Back</Button>
              <Button onClick={generate} disabled={busy || pbBad || !!draft.existing || draft.problems.length > 0} className="bg-[#2D5F4F] hover:bg-[#234A3D]" data-testid="ewb-generate-btn">
                {busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}{draft.route === 'portal' ? 'Save for portal JSON' : viaTally ? 'Send to Tally' : 'Generate e-way bill'}</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default NewEwbDialog;
